import React, { useMemo } from "react";
import { DataLoadingWrapper, SidebarDrawer } from "@jugl-web/ui-components";
import { Avatar } from "@jugl-web/ui-kit";
import { useRestApiProvider } from "@jugl-web/rest-api";
import type { ClientContact } from "@jugl-web/rest-api/contacts";
import { useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { getConversationUsername } from "@web-src/modules/client-support/pages/ClientSupportPage/components/WebChatAvatar/utils";
import { mapContactToContactInfoData } from "../../utils";
import { ContactIntelligenceInfoSection } from "../ContactIntelligenceInfoSection";
import { getContactChannelConnectionStatus } from "../ContactIntelligenceInfoSection/utils";

export type ContactInfoDrawerVariant = "visitorIntelligence" | "info";

export type ContactInfoDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  conversationId?: string;
  conversationName?: string;
  contact?: Partial<ClientContact>;
  variant?: ContactInfoDrawerVariant;
};

export const ContactInfoDrawer: React.FC<ContactInfoDrawerProps> = ({
  isOpen,
  onClose,
  conversationId,
  conversationName,
  contact: providedContact,
  variant = "visitorIntelligence",
}) => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { navigateToPage } = useNavigation();
  const { contactsApi, integrationsApi } = useRestApiProvider();

  const shouldFetchContact =
    isOpen &&
    !!conversationId &&
    (providedContact == null || Object.keys(providedContact).length === 0);

  const {
    data: fetchedContact,
    isFetching,
    isError,
    refetch,
  } = contactsApi.useGetContactByConversationIdQuery(
    shouldFetchContact ? { entityId, conversationId } : skipToken
  );

  const contact = providedContact ?? fetchedContact;

  const {
    data: crmConfig,
    isLoading: isCrmConfigLoading,
    isError: isCrmConfigError,
    refetch: refetchCrmConfig,
  } = integrationsApi.useGetCRMConfigQuery(
    isOpen && entityId ? { entityId } : skipToken
  );

  const channelConnectionStatus = useMemo(
    () =>
      crmConfig ? getContactChannelConnectionStatus(crmConfig) : undefined,
    [crmConfig]
  );

  const { contactName, avatarUsername } = useMemo(() => {
    const name = contact?.name?.trim();
    const fallbackName = conversationName?.trim();
    const displayName =
      name ||
      fallbackName ||
      t({
        id: "contacts-page.anonymous-contact",
        defaultMessage: "Anonymous Contact",
      });

    return {
      contactName: displayName,
      avatarUsername:
        fallbackName && !name
          ? getConversationUsername(fallbackName)
          : displayName,
    };
  }, [contact?.name, conversationName, t]);

  const data = useMemo(
    () => (contact ? mapContactToContactInfoData(contact) : null),
    [contact]
  );

  const isInfoVariant = variant === "info";

  return (
    <SidebarDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t({
        id: "contacts-page.contact-info.title",
        defaultMessage: "Contact Info",
      })}
      className="w-[600px]"
    >
      <DataLoadingWrapper
        isLoading={(shouldFetchContact && isFetching) || isCrmConfigLoading}
        isError={(shouldFetchContact && isError) || isCrmConfigError}
        onRetry={() => {
          if (shouldFetchContact && isError) {
            refetch();
          }

          if (isCrmConfigError) {
            refetchCrmConfig();
          }
        }}
      >
        {data && (
          <SidebarDrawer.Content className="flex flex-col">
            <div className="flex flex-col gap-8">
              <div>
                <div className="flex items-center gap-6 p-8">
                  <Avatar username={avatarUsername} size="4xl" />
                  <span className="text-dark font-secondary truncate text-xl font-semibold leading-5 tracking-[0.2px]">
                    {contactName}
                  </span>
                </div>

                <div className="border-grey-300 w-full border-0 border-b border-solid px-8">
                  <div className="flex w-max flex-col items-center gap-3 pt-2">
                    <div className="flex items-center gap-1 px-5">
                      <span className="text-dark font-secondary text-sm font-medium leading-[160%]">
                        {t({
                          id: "contacts-page.info",
                          defaultMessage: "Info",
                        })}
                      </span>
                    </div>
                    <div className="bg-dark h-1 w-full rounded-lg" />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-8 px-8 pb-8">
                {!!contact?.conversations?.length && (
                  <ContactIntelligenceInfoSection
                    sectionId="channels"
                    values={{ conversations: contact.conversations }}
                    channelConnectionStatus={channelConnectionStatus}
                    onConversationNavigate={({ channel, convId }) => {
                      onClose();
                      navigateToPage("clientSupportActiveConversation", {
                        channel,
                        activeConversation: convId,
                      });
                    }}
                  />
                )}
                <ContactIntelligenceInfoSection
                  sectionId="profile"
                  values={data.profile}
                />
                {!isInfoVariant && (
                  <ContactIntelligenceInfoSection
                    sectionId="visitorIntelligence"
                    values={data.visitorIntelligence}
                  />
                )}
              </div>
            </div>
          </SidebarDrawer.Content>
        )}
      </DataLoadingWrapper>
    </SidebarDrawer>
  );
};
